import { format, isAfter } from "date-fns";
import {
  getDisplayPrettyDate,
  updateFormatAccordingToCountry,
} from "util/methods";

function charToCode(c: string): number {
  return c.charCodeAt(0);
}

function decodeBase64Utf8(base64: string): string {
  return new TextDecoder().decode(Uint8Array.from(atob(base64), charToCode));
}
import AttachmentDomain from "../attachments/attachment-domain";
import { ConversationChannel } from "./conversation-domain";
import MessageFailedReason from "./message-errors";

export enum MESSAGE_ERRORS {
  EXTERNAL_PROVIDER_ERROR = "EXTERNAL_PROVIDER_ERROR",
}

export enum MessageStatus {
  ACCEPTED = "accepted",
  QUEUED = "queued",
  SENDING = "sending",
  SENT = "sent",
  DELIVERED = "delivered",
  UNDELIVERED = "undelivered",
  FAILED = "failed",
  RECEIVING = "receiving",
  RECEIVED = "received",
  READ = "read",
  UNKNOWN = "unknown",
}

export enum StatusIcon {
  IN_PROGRESS = "in_progress", // GRAY
  DELIVERED = "delivered",
  SUCCESS = "success", // BLUE
  FAILED = "failed", // RED
  NONE = "none", // DON'T SHOW
}

export enum MessageType {
  TEXT = "TEXT",
  NOTIFICATION = "NOTIFICATION",
  INFO_CARD = "INFO_CARD",
  TEMPLATE = "TEMPLATE",
}

export enum MessageSubType {
  REGULAR = "REGULAR",
  WEBSITE_ENQUIRY = "WEBSITE_ENQUIRY",
  FINISHED_CALL = "FINISHED_CALL",
  MISSED_CALL = "MISSED_CALL",
  NEW_SUBJECT_LINE = "NEW_SUBJECT_LINE",
  NEW_LEAD = "NEW_LEAD",
  VEHICLE = "VEHICLE",
  EXISTING_LEAD_EXTRA_CHANNEL = "EXISTING_LEAD_EXTRA_CHANNEL",
  CAMPAIGN_MESSAGE = "CAMPAIGN_MESSAGE",
  PAYMENT_REQUEST = "PAYMENT_REQUEST",
  PAYMENT_RECEIVED = "PAYMENT_RECEIVED",
  REVIEW_REQUEST = "REVIEW_REQUEST",
  REVIEW_SUBMITTED = "REVIEW_SUBMITTED",
  NEW_AGENT = "NEW_AGENT",
  AUTOMATION_SEQUENCE_EXCLUSION = "AUTOMATION_SEQUENCE_EXCLUSION",
}

export enum AttachmentType {
  AUDIO = "audio",
  VIDEO = "video",
  IMAGE = "image",
  FILE = "file",
}

export enum MessageDirection {
  INCOMING = "incoming",
  OUTGOING = "outgoing",
  UNSPECIFIED = "unspecified",
}

export interface MessageMetadata {
  error_code?: string;
  error_description?: string;
  attributes?: {
    [key: string]: string | number;
  };
}

const convertMimeTypeToType = (
  mimeType?: string
): AttachmentType | undefined => {
  if (!mimeType) {
    return undefined;
  }
  if (mimeType.includes("audio")) {
    return AttachmentType.AUDIO;
  }
  if (mimeType.includes("video")) {
    return AttachmentType.VIDEO;
  }
  if (mimeType.includes("image")) {
    return AttachmentType.IMAGE;
  }
  return AttachmentType.FILE;
};

export enum MessageButtonType {
  URL = "URL",
  PHONE_NUMBER = "PHONE_NUMBER",
  QUICK_REPLY = "QUICK_REPLY",
}

export class MessageButton {
  id: string;
  type: MessageButtonType;
  text: string;
  url?: string;
  phoneNumber?: string;

  constructor(
    type: MessageButtonType,
    text: string,
    url?: string,
    phoneNumber?: string
  ) {
    this.id = crypto.randomUUID();
    this.type = type;
    this.text = text;
    this.url = url;
    this.phoneNumber = phoneNumber;
  }
}

export interface ReplyToMessage {
  id: number | null;
  body: string;
  isIncoming: boolean | null;
  senderName: string | null;
  attachments: { url: string; type: string }[];
}

// One reactor's current incoming WhatsApp reaction to this message.
export interface MessageReaction {
  emoji: string;
  reactorHandle: string;
  reactorName: string | null;
  reactedAt: Date;
}

export interface MessageReactionSummary {
  emoji: string;
  reactors: string[];
}

export default class MessageDomain {
  id: number;

  body: string;

  isIncoming: boolean;

  createdAt: Date;

  conversationId: number;

  agentId: number;

  status: MessageStatus;

  type: MessageType;

  subType: MessageSubType;

  title: string | null;

  replyToMessageId: number | null;

  replyToMessage: ReplyToMessage | null;

  canReply: boolean;

  reactions: MessageReaction[];

  // Orders reaction snapshots of this message (0 = never reacted to). A higher
  // revision is always the newer state, withdrawals included.
  reactionsRevision: number;

  direction: MessageDirection;

  metadata: MessageMetadata | null;

  attachments: AttachmentDomain[];

  rawHtml: string | null;

  buttons?: MessageButton[];

  automationRunId?: string;

  aiAssistantId?: string;

  campaignId?: string;

  getStatusIcon(channel: ConversationChannel): StatusIcon {
    if (!this.isIncoming) {
      switch (this.status) {
        case MessageStatus.UNDELIVERED:
        case MessageStatus.FAILED:
          return StatusIcon.FAILED;
        case MessageStatus.ACCEPTED:
        case MessageStatus.QUEUED:
        case MessageStatus.SENDING:
        case MessageStatus.SENT:
          return channel === ConversationChannel.FACEBOOK ||
            channel === ConversationChannel.INSTAGRAM
            ? StatusIcon.DELIVERED
            : StatusIcon.IN_PROGRESS;
        case MessageStatus.DELIVERED:
          return StatusIcon.DELIVERED;
        case MessageStatus.READ:
          return StatusIcon.SUCCESS;
        default:
          return StatusIcon.NONE;
      }
    }

    return StatusIcon.NONE;
  }

  isNotification(): boolean {
    return (
      this.type === MessageType.NOTIFICATION ||
      this.subType === MessageSubType.NEW_SUBJECT_LINE
    );
  }

  isPhoneCall(): boolean {
    return (
      this.subType === MessageSubType.FINISHED_CALL ||
      this.subType === MessageSubType.MISSED_CALL
    );
  }

  isPhoneCallWithoutRecording(): boolean {
    return (
      this.isPhoneCall() && this.getAttachmentType() !== AttachmentType.AUDIO
    );
  }

  isPhoneCallRecording(): boolean {
    return (
      this.isPhoneCall() && this.getAttachmentType() === AttachmentType.AUDIO
    );
  }

  isImageMessage(): boolean {
    return (
      this.getAttachmentType() === AttachmentType.IMAGE && !this.isVehicleInfo()
    );
  }

  isCustomerLeadMessage(): boolean {
    return this.subType === MessageSubType.WEBSITE_ENQUIRY;
  }

  isVideoMessage(): boolean {
    return this.getAttachmentType() === AttachmentType.VIDEO;
  }

  isFileMessage(): boolean {
    return this.getAttachmentType() === AttachmentType.FILE;
  }

  isAudioMessage(): boolean {
    return (
      this.getAttachmentType() === AttachmentType.AUDIO && !this.isPhoneCall()
    );
  }

  hasMultipleAttachments(): boolean {
    return this.attachments.length > 1 && !this.isVehicleInfo();
  }

  isForwardedEmail(): boolean {
    return this.body === "{ Forwarded Message }" && this.rawHtml !== null;
  }

  isTextMessage(): boolean {
    const isNotTextMessage =
      this.isAudioMessage() ||
      this.isImageMessage() ||
      this.isVideoMessage() ||
      this.isFileMessage() ||
      this.isPhoneCallWithoutRecording() ||
      this.isPhoneCallRecording() ||
      this.hasMultipleAttachments() ||
      this.isVehicleInfo() ||
      this.isForwardedEmail();

    return !isNotTextMessage && !!this.body;
  }

  isInfoMessage(): boolean {
    return (
      this.type === MessageType.INFO_CARD &&
      this.subType === MessageSubType.REGULAR &&
      !this.body &&
      !this.attachments.length &&
      !!this.title &&
      !!this.metadata?.attributes &&
      Object.keys(this.metadata?.attributes).length > 0
    );
  }

  // Display name of the group participant who sent this message. The backend
  // stamps sender attribution into metadata.attributes on incoming WhatsApp
  // group messages (group_participant_name / _wa_id / _user_id); all three
  // are absent on 1:1 conversations and outgoing messages.
  getGroupSenderName(): string | null {
    const attributes = this.metadata?.attributes;
    if (!attributes) {
      return null;
    }
    const name =
      attributes.group_participant_name ||
      attributes.group_participant_wa_id ||
      attributes.group_participant_user_id;
    return name ? String(name) : null;
  }

  // Only the WhatsApp profile name, never the wa_id/BSUID fallback. Null means
  // the sender shared no display name — the avatar then shows the generic
  // silhouette instead of deriving initials from a phone number ("4"), and the
  // sender label keeps its neutral colour instead of a hash of a digit.
  getGroupSenderProfileName(): string | null {
    const name = this.metadata?.attributes?.group_participant_name;
    return name ? String(name) : null;
  }

  // Stable identifier of the group participant who sent this message, used to
  // group consecutive bubbles by sender. Null outside group conversations, so
  // 1:1 messages collapse into a single (null === null) group.
  getGroupSenderId(): string | null {
    const attributes = this.metadata?.attributes;
    if (!attributes) {
      return null;
    }
    const identifier =
      attributes.group_participant_wa_id ||
      attributes.group_participant_user_id;
    return identifier ? String(identifier) : null;
  }

  getAttachmentType(): AttachmentType | undefined {
    if (this.attachments.length === 1) {
      return convertMimeTypeToType(this.attachments[0].type);
    }

    return undefined;
  }

  isVehicleInfo(): boolean {
    return this.subType === MessageSubType.VEHICLE;
  }

  hasURLs(): boolean {
    if (this.body.length === 0) {
      return false;
    }

    return (
      (this.body.includes("http") ||
        this.body.includes("www.") ||
        this.body.includes("@")) &&
      !this.isVehicleInfo()
    );
  }

  getTimeStamp(): string {
    return getDisplayPrettyDate(this.createdAt);
  }

  getTimeOnly(): string {
    return format(this.createdAt, "hh:mm a");
  }

  getFullDateTime(): string {
    return format(
      this.createdAt,
      updateFormatAccordingToCountry("eeee, d MMMM yyyy 'at' hh:mm a")
    );
  }

  getFontColor(): string {
    // Outgoing bubbles sit on `colorPalette.solid` — use `fg.inverted` so the
    // text is white in light mode (high contrast on saturated brand) and dark
    // in dark mode (avoids harsh white-on-saturated-blue blast).
    return this.isIncoming || this.isPhoneCall() ? "inherit" : "fg.inverted";
  }

  getFailedReason(): string {
    return this.metadata?.error_description! || MessageFailedReason.default;
  }

  isUnreachable(): boolean {
    if (
      !this.metadata ||
      this.metadata.error_code !== MESSAGE_ERRORS.EXTERNAL_PROVIDER_ERROR ||
      !this.metadata.error_description ||
      this.direction !== MessageDirection.OUTGOING
    ) {
      return false;
    }

    try {
      const providerErrors = JSON.parse(this.metadata.error_description);

      if (!Array.isArray(providerErrors)) {
        return false;
      }

      const hasUnreachableError = providerErrors.some(
        (providerError) =>
          // eslint-disable-next-line
          providerError.hasOwnProperty("code") && providerError.code === 131026 // Whatsapp Code for "Customer doesn't exist"
      );

      return hasUnreachableError;
    } catch (_error: unknown) {
      return false;
    }
  }

  isCreatedAfter(date: Date | null): boolean {
    if (!date) {
      return true;
    }

    return isAfter(this.createdAt, date);
  }

  getHtml(): string | null {
    if (!this.rawHtml) {
      return null;
    }

    return decodeBase64Utf8(this.rawHtml);
  }

  // Reactions grouped by emoji in the order each emoji first appeared, naming
  // every reactor. A reactor without a profile name is shown by handle.
  getReactionSummary(): MessageReactionSummary[] {
    const reactorsByEmoji = new Map<string, string[]>();
    for (const reaction of this.reactions) {
      const reactors = reactorsByEmoji.get(reaction.emoji) ?? [];
      reactors.push(reaction.reactorName || reaction.reactorHandle);
      reactorsByEmoji.set(reaction.emoji, reactors);
    }
    return Array.from(reactorsByEmoji, toReactionSummary);
  }

  // A copy carrying the given reactions: the reaction socket event changes
  // nothing else about the message, so the cached copy keeps its other state.
  withReactions(
    reactions: MessageReaction[],
    reactionsRevision: number
  ): MessageDomain {
    return Object.assign(Object.create(Object.getPrototypeOf(this)), this, {
      reactions,
      reactionsRevision,
    });
  }

  // This message, keeping `previous`'s reactions when they are the newer
  // state. Socket payloads and reads can arrive out of order, so an older
  // snapshot of the message must not undo a newer reaction change.
  withNewerReactionsOf(previous: MessageDomain): MessageDomain {
    return previous.reactionsRevision > this.reactionsRevision
      ? this.withReactions(previous.reactions, previous.reactionsRevision)
      : this;
  }

  toReplyPreview(agentName?: string): ReplyToMessage {
    return {
      id: this.id,
      body: this.body,
      isIncoming: this.isIncoming,
      senderName:
        this.getGroupSenderName() ||
        (!this.isIncoming ? agentName?.trim() || null : null),
      attachments: this.attachments.map(replyAttachment),
    };
  }

  constructor(
    id: number,
    body: string,
    isIncoming: boolean,
    createdAt: Date,
    conversationId: number,
    agentId: number,
    status: MessageStatus,
    type: MessageType,
    subType: MessageSubType,
    title: string | null,
    replyToMessageId: number | null,
    direction: MessageDirection,
    metadata: MessageMetadata | null,
    attachments: AttachmentDomain[],
    rawHtml: string | null,
    buttons: MessageButton[] | null,
    aiAssistantId?: string,
    automationRunId?: string,
    campaignId?: string,
    replyToMessage: ReplyToMessage | null = null,
    canReply = false,
    reactions: MessageReaction[] = [],
    reactionsRevision = 0
  ) {
    this.id = id;
    this.body = body;
    this.isIncoming = isIncoming;
    this.createdAt = createdAt;
    this.conversationId = conversationId;
    this.agentId = agentId;
    this.status = status;
    this.type = type;
    this.subType = subType;
    this.title = title;
    this.replyToMessageId = replyToMessageId;
    this.replyToMessage = replyToMessage;
    this.canReply = canReply;
    this.reactions = reactions;
    this.reactionsRevision = reactionsRevision;
    this.direction = direction;
    this.metadata = metadata;
    this.attachments = attachments;
    this.rawHtml = rawHtml;
    this.buttons = buttons || undefined;
    this.aiAssistantId = aiAssistantId;
    this.automationRunId = automationRunId;
    this.campaignId = campaignId;
  }
}

function toReactionSummary([emoji, reactors]: [
  string,
  string[],
]): MessageReactionSummary {
  return { emoji, reactors };
}

function replyAttachment(attachment: AttachmentDomain): {
  url: string;
  type: string;
} {
  return { url: attachment.url, type: attachment.type };
}
